import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@agconnections/grow-ui';
import { useHistory } from 'react-router-dom';

import { useStore } from 'components/Store';
import { useAuth } from 'utilities/base-auth';
import { paths } from 'routes/paths';
import { capitalizeFullName } from 'helpers/textHelpers';
import { OperationType } from 'components/RecordsListView/constants';
import useOperationPdf, {
  OperationExport
} from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';

interface ExportOperationPdfProps {
  operationType: OperationType;
  operationId: string;
  operationDetails: OperationExport;
  isLoadingDetails: boolean;
  errors: string | null;
  notification: (
    type: 'CREATE_SUCCESS' | 'CREATE_OR_EDIT_ERROR' | 'PDF_EXPORT_ERROR',
    ...args: any[]
  ) => void;
}

const ExportOperationPdf: React.FC<ExportOperationPdfProps> = ({
  operationType,
  operationId,
  operationDetails,
  isLoadingDetails,
  errors,
  notification
}) => {
  const history = useHistory();
  const { user } = useAuth();
  const { state: { organization } = {} } = useStore() || {};

  const isWorkOrder = operationType === OperationType.WORK_ORDER;
  const operationLabel = isWorkOrder ? 'work order' : 'application';

  const texts = {
    notFoundTitle: `${capitalizeFullName(operationLabel)} Not Found`,
    idLabel: `${capitalizeFullName(operationLabel)} ID`,
    redirectingMessage: `This ${operationLabel} either doesn't exist or belongs to a different organization. Redirecting to ${operationLabel}s list...`,
    goNowButtonText: `Go to ${capitalizeFullName(operationLabel)}s Now`,
    initializingText: `Initializing ${operationLabel} export...`,
    loadingText: `Loading ${operationLabel}...`
  };

  const listPath = isWorkOrder ? paths.workOrders : paths.applications;

  const [isExporting, setIsExporting] = useState(false);
  const [hasDownloadStarted, setHasDownloadStarted] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [hasAttemptedExport, setHasAttemptedExport] = useState(false);
  const isMountedRef = useRef(true);
  const timeoutIdsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const scheduleRedirect = useCallback(
    (delay: number) => {
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          history.push(listPath);
        }
        timeoutIdsRef.current = timeoutIdsRef.current.filter(
          id => id !== timeoutId
        );
      }, delay);

      timeoutIdsRef.current.push(timeoutId);
    },
    [history, listPath]
  );

  const { exportPdfEnglish, downloadingPDF } = useOperationPdf({
    operationType,
    onError: () => {
      if (isMountedRef.current) {
        notification('PDF_EXPORT_ERROR');
        scheduleRedirect(2000);
        setIsExporting(false);
        setHasDownloadStarted(false);
      }
    }
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      timeoutIdsRef.current.forEach(clearTimeout);
      timeoutIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isLoadingDetails) {
      setHasAttemptedFetch(true);
    }
  }, [isLoadingDetails]);

  useEffect(() => {
    if (
      hasAttemptedFetch &&
      !isLoadingDetails &&
      !operationDetails?.id &&
      isMountedRef.current
    ) {
      scheduleRedirect(2000);
    }
  }, [hasAttemptedFetch, isLoadingDetails, operationDetails, scheduleRedirect]);

  useEffect(() => {
    if (
      operationDetails?.id &&
      !isExporting &&
      !hasAttemptedExport &&
      isMountedRef.current
    ) {
      setHasAttemptedExport(true);
      setIsExporting(true);
      setHasDownloadStarted(false);

      exportPdfEnglish(operationDetails);
    }
  }, [
    operationDetails,
    isExporting,
    hasAttemptedExport,
    exportPdfEnglish,
    notification,
    scheduleRedirect
  ]);

  useEffect(() => {
    if (isExporting && downloadingPDF && !hasDownloadStarted) {
      setHasDownloadStarted(true);
    }
  }, [isExporting, downloadingPDF, hasDownloadStarted]);

  useEffect(() => {
    if (
      isExporting &&
      hasDownloadStarted &&
      !downloadingPDF &&
      operationDetails?.id &&
      isMountedRef.current
    ) {
      notification('CREATE_SUCCESS');
      setIsExporting(false);
      setHasDownloadStarted(false);
      scheduleRedirect(1500);
    }
  }, [
    isExporting,
    hasDownloadStarted,
    downloadingPDF,
    operationDetails,
    notification,
    scheduleRedirect
  ]);

  if (!organization?.id) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <div className="mt-4 text-lg font-semibold">
            {texts.initializingText}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <div className="mt-4 text-lg font-semibold">
            Loading authentication context...
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingDetails) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <div className="mt-4 text-lg font-semibold">{texts.loadingText}</div>
        </div>
      </div>
    );
  }

  if (hasAttemptedFetch && !isLoadingDetails && !operationDetails?.id) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-10">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <div className="text-xl font-semibold mb-4">
            {texts.notFoundTitle}
          </div>
          <div className="text-gray-600 mb-2">
            Current Organization: <strong>{organization?.name}</strong>
          </div>
          <div className="text-gray-600 mb-4">
            {texts.idLabel}: <strong>{operationId}</strong>
          </div>
          <div className="text-gray-600 mb-6">{texts.redirectingMessage}</div>
          {errors && (
            <div className="text-sm text-red-600 mb-4 p-2 bg-red-50 rounded">
              Error: {errors}
            </div>
          )}
          <Button type="primary" onClick={() => history.push(listPath)}>
            {texts.goNowButtonText}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen bg-neutral-10">
      <div className="text-center bg-white p-8 rounded-lg shadow-md">
        <Spinner size="xl" />
        <div className="mt-4 text-lg font-semibold">Generating PDF...</div>
        <div className="mt-2 text-sm text-gray-600">
          {operationDetails?.name}
        </div>
      </div>
    </div>
  );
};

export default ExportOperationPdf;
